import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Clock, FileText, Presentation, Users, AlertCircle, ExternalLink, FolderOpen, Loader2, Eye, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const FORMAT_LABEL_FR = { C: "Court", L: "Long", D: "Dédié" };
const FORMAT_LABEL_EN = { C: "Short", L: "Long", D: "Dedicated" };

const ROLE_LEVEL = { invite: 0, membre: 1, consultant: 2, president: 2, dr: 3, dz: 3, dn: 4, ceo: 5 };
const NIVEAU_REQ = { Membre: 1, Consultant: 2, DR: 3, DN: 4 };

export default function AtelierDetailPage() {
  const { atelierId } = useParams();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [atelier, setAtelier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [previewing, setPreviewing] = useState(null); // type currently loading for preview
  const [previewState, setPreviewState] = useState(null); // {url, name, type}
  const uiLocale = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
  const fmtLabel = uiLocale === "en" ? FORMAT_LABEL_EN : FORMAT_LABEL_FR;

  useEffect(() => {
    api.get(`/ateliers/${atelierId}`).then((r) => setAtelier(r.data)).finally(() => setLoading(false));
  }, [atelierId]);

  if (loading) return <div className="h-96 bg-bg-soft rounded-xl animate-pulse" data-testid="atelier-loading" />;
  if (!atelier) return <div className="text-center py-20 text-text-secondary">{uiLocale === "en" ? "Workshop not found" : "Atelier introuvable"}</div>;

  const userLevel = ROLE_LEVEL[user?.role] || 0;
  const canAccessScript = userLevel >= (NIVEAU_REQ[atelier.niveau_animateur] || 3);
  const title = uiLocale === "en"
    ? (atelier.title_en || atelier.title_fr || atelier.id)
    : (atelier.title_fr || atelier.title_en || atelier.id);

  const downloadFile = async (type, label) => {
    setDownloading(type);
    try {
      const token = localStorage.getItem("delphi_token");
      const res = await fetch(`${API_BASE}/ateliers/${atelierId}/file?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let detail = "";
        try {
          const err = await res.json();
          detail = err?.detail || err?.message || "";
        } catch {
          detail = await res.text().catch(() => "");
        }
        toast.error(`${uiLocale === "en" ? "Error" : "Erreur"} ${res.status}${detail ? " : " + detail : ""}`, { duration: 8000 });
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `${atelierId}-${type}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${label} ${uiLocale === "en" ? "downloaded" : "téléchargé"}`);
    } catch (e) {
      toast.error(`${uiLocale === "en" ? "Network error" : "Erreur réseau"} : ${e?.message || (uiLocale === "en" ? "unknown" : "inconnue")}`);
    } finally {
      setDownloading(null);
    }
  };

  /**
   * Inline preview for PDFs. Streams the file as a blob (auth-protected) and
   * mounts a fullscreen iframe. The blob is a `Content-Disposition: inline`
   * application/pdf, so the browser's native PDF viewer renders it directly.
   */
  const previewPdf = async (type, label) => {
    setPreviewing(type);
    try {
      const token = localStorage.getItem("delphi_token");
      const res = await fetch(`${API_BASE}/ateliers/${atelierId}/file?type=${type}&inline=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(`${uiLocale === "en" ? "Error" : "Erreur"} ${res.status}${err?.detail ? " : " + err.detail : ""}`, { duration: 8000 });
        return;
      }
      const blob = await res.blob();
      if (blob.type !== "application/pdf") {
        toast.error(uiLocale === "en"
          ? "Preview only available for PDFs. Use Download instead."
          : "Aperçu disponible uniquement pour les PDFs. Utilisez le téléchargement.");
        return;
      }
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `${atelierId}-${type}.pdf`;
      const url = URL.createObjectURL(blob);
      // Clean up the previous blob URL if any
      setPreviewState((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { url, name: filename, label };
      });
    } catch (e) {
      toast.error(`${uiLocale === "en" ? "Network error" : "Erreur réseau"} : ${e?.message || "?"}`);
    } finally {
      setPreviewing(null);
    }
  };

  const closePreview = () => {
    setPreviewState((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const gateMsg = uiLocale === "en"
    ? `Reserved for roles ≥ ${atelier.niveau_animateur}`
    : `Réservé aux rôles ≥ ${atelier.niveau_animateur}`;
  const supports = [
    { key: "ppt_fr", label: uiLocale === "en" ? "PPT (FR)" : "PPT (FR)", link: atelier.gdrive_ppt_fr, icon: Presentation, allowed: true },
    { key: "script_fr", label: uiLocale === "en" ? "Facilitator script (FR)" : "Script animateur (FR)", link: atelier.gdrive_script_fr, icon: FileText, allowed: canAccessScript, gate: gateMsg },
    { key: "ppt_en", label: uiLocale === "en" ? "PPT (EN)" : "PPT (EN)", link: atelier.gdrive_ppt_en, icon: Presentation, allowed: true },
    { key: "script_en", label: uiLocale === "en" ? "Facilitator script (EN)" : "Script animateur (EN)", link: atelier.gdrive_script_en, icon: FileText, allowed: canAccessScript, gate: gateMsg },
  ];
  const availableSupports = supports.filter((s) => s.link);

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="atelier-detail-page">
      <Link to="/catalogue" className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-apollo-orange-dark" data-testid="back-to-catalogue">
        <ArrowLeft className="w-4 h-4" /> {t("atelier.back_to_library")}
      </Link>

      <header className="bg-white border border-border rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="font-mono text-sm px-2.5 py-1 rounded bg-bg-soft border border-border text-text-secondary" data-testid="atelier-detail-id">{atelier.id}</span>
          <Badge className="bg-apollo-orange-light text-apollo-orange-dark border-0">{atelier.domain}</Badge>
          <Badge variant="outline">{fmtLabel[atelier.format]}</Badge>
          <span className="inline-flex items-center gap-1 text-sm text-text-secondary"><Clock className="w-3.5 h-3.5" /> {atelier.duree_minutes} {t("atelier.minutes")}</span>
          {atelier.status !== "published" && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-warning/10 text-warning">{atelier.status}</span>
          )}
        </div>
        <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter text-text-primary mb-3" data-testid="atelier-title">{title}</h1>
        {atelier.sub_domain && <div className="text-sm text-apollo-orange-dark font-medium mb-2">{atelier.sub_domain}</div>}
        {atelier.description && (
          <p className="font-oracle italic text-lg md:text-xl text-text-primary/80 leading-relaxed">« {atelier.description} »</p>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="bg-white border border-border rounded-xl p-6" data-testid="when-to-do">
          <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-apollo-orange" />{uiLocale === "en" ? "When to run it" : "Quand le faire"}</h2>
          {atelier.symptomes_traites?.length > 0 && (
            <div className="mb-3">
              <div className="text-xs uppercase tracking-wider text-text-secondary mb-1.5">{t("atelier.symptoms_treated")}</div>
              <div className="flex flex-wrap gap-1.5">
                {atelier.symptomes_traites.map((s) => (
                  <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-apollo-orange-light text-apollo-orange-dark font-medium">{s}</span>
                ))}
              </div>
            </div>
          )}
          {atelier.phase_groupe?.length > 0 && (
            <div className="mb-3">
              <div className="text-xs uppercase tracking-wider text-text-secondary mb-1.5">{uiLocale === "en" ? "Group phase" : "Phase du groupe"}</div>
              <div className="text-sm">{atelier.phase_groupe.map((p) => t(`phase.${p}`, p)).join(", ")}</div>
            </div>
          )}
          {atelier.profil_bpm_cible?.length > 0 && (
            <div className="mb-3">
              <div className="text-xs uppercase tracking-wider text-text-secondary mb-1.5">{uiLocale === "en" ? "Target BPM profiles" : "Profils BPM cibles"}</div>
              <div className="text-sm">{atelier.profil_bpm_cible.join(", ")}</div>
            </div>
          )}
          {atelier.kpis_cibles?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-text-secondary mb-1.5">{uiLocale === "en" ? "Target KPIs" : "KPI ciblés"}</div>
              <div className="flex flex-wrap gap-1">
                {atelier.kpis_cibles.map((k) => (
                  <span key={k} className="text-[10px] font-mono px-2 py-0.5 rounded bg-bg-soft border border-border">{k}</span>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="bg-white border border-border rounded-xl p-6" data-testid="preparation">
          <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-apollo-orange" />{uiLocale === "en" ? "Preparation" : "Préparation"}</h2>
          <div className="space-y-2 text-sm">
            <div><span className="text-text-secondary">{uiLocale === "en" ? "Facilitator" : "Animateur"} : </span><span className="font-medium">{atelier.niveau_animateur}</span></div>
            {atelier.niveau_membre && <div><span className="text-text-secondary">{uiLocale === "en" ? "Member level" : "Niveau membre"} : </span><span className="font-medium">{atelier.niveau_membre}</span></div>}
            <div><span className="text-text-secondary">{uiLocale === "en" ? "Expected effect" : "Effet attendu"} : </span><span className="font-medium">{atelier.effet_attendu}</span></div>
            <div><span className="text-text-secondary">{uiLocale === "en" ? "Family" : "Famille"} : </span><span className="font-medium">{atelier.family}</span></div>
            {atelier.prerequis?.length > 0 && (
              <div>
                <span className="text-text-secondary">{uiLocale === "en" ? "Prerequisites" : "Prérequis"} : </span>
                <span className="font-medium font-mono text-xs">{atelier.prerequis.join(", ")}</span>
              </div>
            )}
            {atelier.supports_disponibles?.length > 0 && (
              <div>
                <span className="text-text-secondary">{uiLocale === "en" ? "Materials" : "Supports"} : </span>
                <span className="font-medium">{atelier.supports_disponibles.join(" + ")}</span>
              </div>
            )}
          </div>
        </section>
      </div>

      <section className="bg-white border border-border rounded-xl p-6" data-testid="supports-section">
        <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-apollo-orange" />{uiLocale === "en" ? "Workshop materials" : "Supports d'atelier"}
        </h2>
        {availableSupports.length === 0 ? (
          <p className="text-sm text-text-secondary italic">{uiLocale === "en" ? "No Drive link set for this workshop. The CEO will add them soon." : "Aucun lien Drive renseigné pour cet atelier. Le CEO les ajoutera bientôt."}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {availableSupports.map((s) => {
              const Icon = s.icon;
              const isLoading = downloading === s.key;
              const isPreviewLoading = previewing === s.key;
              const disabled = !s.allowed;
              const openLabel = uiLocale === "en" ? "Open" : "Ouvrir";
              const previewLabel = uiLocale === "en" ? "Preview" : "Aperçu";
              const downloadLabel = uiLocale === "en" ? "Download" : "Télécharger";
              const scriptWord = uiLocale === "en" ? "script" : "le script";
              const pptWord = uiLocale === "en" ? "the PPT" : "le PPT";
              const isScript = s.key.startsWith("script");
              return (
                <div key={s.key} className="inline-flex items-stretch rounded-md overflow-hidden" data-testid={`support-group-${s.key}`}>
                  <Button
                    onClick={() => s.allowed && (isScript ? previewPdf(s.key, s.label) : downloadFile(s.key, s.label))}
                    disabled={disabled || isLoading || isPreviewLoading}
                    variant={isScript ? "outline" : "default"}
                    className={`${isScript ? "border-apollo-orange-dark/30 text-apollo-orange-dark hover:bg-apollo-orange-light/50" : "bg-apollo-orange hover:bg-apollo-orange-dark text-white"} ${isScript ? "rounded-r-none border-r-0" : ""}`}
                    data-testid={`btn-${isScript ? "preview" : "download"}-${s.key}`}
                    title={disabled ? s.gate : `${isScript ? previewLabel : openLabel} ${s.label}`}
                  >
                    {isPreviewLoading || isLoading
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : isScript ? <Eye className="w-4 h-4 mr-2" /> : <Icon className="w-4 h-4 mr-2" />}
                    {isScript
                      ? `${previewLabel} ${scriptWord} ${s.key.endsWith("en") ? "EN" : "FR"}`
                      : `${openLabel} ${pptWord} ${s.key.endsWith("en") ? "EN" : "FR"}`}
                    {disabled && <span className="ml-2 text-[10px] font-mono">🔒</span>}
                  </Button>
                  {isScript && !disabled && (
                    <Button
                      onClick={() => downloadFile(s.key, s.label)}
                      disabled={isLoading || isPreviewLoading}
                      variant="outline"
                      className="border-apollo-orange-dark/30 text-apollo-orange-dark hover:bg-apollo-orange-light/50 rounded-l-none px-2"
                      data-testid={`btn-download-${s.key}`}
                      title={downloadLabel}
                    >
                      <FileText className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
            {atelier.gdrive_folder && (
              <a href={atelier.gdrive_folder} target="_blank" rel="noreferrer" data-testid="link-gdrive-folder">
                <Button variant="ghost" className="text-text-secondary">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> {uiLocale === "en" ? "Drive folder" : "Dossier Drive"}
                </Button>
              </a>
            )}
          </div>
        )}
        {availableSupports.some((s) => !s.allowed) && (
          <p className="text-xs text-text-secondary mt-3 italic" data-testid="script-gate-message">
            🔒 {uiLocale === "en"
              ? `The facilitator script is reserved for roles ≥ ${atelier.niveau_animateur} (you: ${user?.role_label_fr}).`
              : `Le script animateur est réservé aux rôles ≥ ${atelier.niveau_animateur} (vous : ${user?.role_label_fr}).`}
          </p>
        )}
      </section>

      {/* Inline PDF preview modal */}
      {previewState && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex flex-col p-4 md:p-8"
          onClick={closePreview}
          data-testid="pdf-preview-modal"
        >
          <div
            className="flex-1 bg-white rounded-2xl overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-bg-soft">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-apollo-orange-dark flex-shrink-0" />
                <span className="font-medium text-sm truncate" data-testid="pdf-preview-name">{previewState.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={previewState.url}
                  download={previewState.name}
                  className="text-xs font-medium px-3 py-1.5 rounded-md border border-border hover:bg-white text-text-primary inline-flex items-center gap-1.5"
                  data-testid="pdf-preview-download"
                >
                  <FileText className="w-3.5 h-3.5" />
                  {uiLocale === "en" ? "Download" : "Télécharger"}
                </a>
                <button
                  onClick={closePreview}
                  className="p-1.5 rounded-md hover:bg-white text-text-secondary"
                  data-testid="pdf-preview-close"
                  aria-label={uiLocale === "en" ? "Close preview" : "Fermer l'aperçu"}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <iframe
              src={previewState.url}
              title={previewState.name}
              className="flex-1 w-full"
              data-testid="pdf-preview-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
